class CustomSelectBox {
  constructor(selectElement) {
    this.originalSelect = selectElement;
    this.customSelect = null;
    this.options = [];
    this.isOpen = false;

    this.init();
  }

  init() {
    this.createCustomSelect();
    this.originalSelect.style.display = 'none';
    this.originalSelect.parentNode.insertBefore(
      this.customSelect,
      this.originalSelect.nextSibling
    );
    this.bindEvents();
    this.observeOriginalSelect();
  }

  createCustomSelect() {
    this.customSelect = document.createElement('div');
    this.customSelect.className = 'custom-select-container';

    const trigger = document.createElement('div');
    trigger.className = 'select-trigger';

    const selectedOptionText = this.originalSelect.options[this.originalSelect.selectedIndex]?.text || 'Select an option';
    const selected = document.createElement('span');
    selected.className = 'selected';
    selected.textContent = selectedOptionText;
    selected.title = selectedOptionText;

    const arrow = document.createElement('div');
    arrow.className = 'arrow';

    trigger.appendChild(selected);
    trigger.appendChild(arrow);

    const optionsContainer = document.createElement('div');
    optionsContainer.className = 'options-container';

    Array.from(this.originalSelect.options).forEach(option => {
      const optionElement = document.createElement('div');
      optionElement.className = 'option';
      optionElement.dataset.value = option.value;

      const textSpan = document.createElement('span');
      textSpan.textContent = option.text;
      textSpan.title = option.text;

      optionElement.appendChild(textSpan);

      if (option.selected) {
        optionElement.classList.add('selected');
      }

      this.options.push(optionElement);
      optionsContainer.appendChild(optionElement);
    });

    this.customSelect.appendChild(trigger);
    this.customSelect.appendChild(optionsContainer);
  }

  calculateOptionsHeight() {
    const optionsContainer = this.customSelect.querySelector('.options-container');
    const trigger = this.customSelect.querySelector('.select-trigger');

    const triggerRect = trigger.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const bottomSpace = viewportHeight - triggerRect.bottom;

    const optionHeight = 40;
    const minHeight = optionHeight * 5;
    const maxHeight = bottomSpace - 20;

    if (maxHeight < minHeight) {
      const topSpace = triggerRect.top;
      const topMaxHeight = topSpace - 20;

      optionsContainer.style.maxHeight = `${Math.max(minHeight, topMaxHeight)}px`;
      optionsContainer.style.top = 'auto';
      optionsContainer.style.bottom = '100%';
      optionsContainer.style.borderTop = '1px solid #ccc';
      optionsContainer.style.borderBottom = 'none';
      optionsContainer.style.borderRadius = '4px 4px 0 0';
      trigger.classList.add('top');
    } else {
      optionsContainer.style.maxHeight = `${Math.max(minHeight, maxHeight)}px`;
      optionsContainer.style.top = '100%';
      optionsContainer.style.bottom = 'auto';
      optionsContainer.style.borderTop = 'none';
      optionsContainer.style.borderBottom = '1px solid #ccc';
      optionsContainer.style.borderRadius = '0 0 4px 4px';
      trigger.classList.remove('top');
    }
  }

  bindEvents() {
    const trigger = this.customSelect.querySelector('.select-trigger');
    const optionsContainer = this.customSelect.querySelector('.options-container');
    const selected = this.customSelect.querySelector('.selected');

    trigger.addEventListener('click', (e) => {
      e.stopPropagation();
      this.isOpen = !this.isOpen;
      trigger.classList.toggle('active');
      optionsContainer.classList.toggle('active');

      if (this.isOpen) {
        this.calculateOptionsHeight();
      }
    });

    window.addEventListener('resize', () => {
      if (this.isOpen) {
        this.calculateOptionsHeight();
      }
    });

    this.options.forEach(option => {
      option.addEventListener('click', () => {
        const optionText = option.querySelector('span').textContent;
        selected.textContent = optionText;
        selected.title = optionText;
        this.options.forEach(opt => opt.classList.remove('selected'));
        option.classList.add('selected');
        this.originalSelect.value = option.dataset.value;

        const event = new Event('change');
        this.originalSelect.dispatchEvent(event);

        this.closeDropdown();
      });
    });

    document.addEventListener('click', () => {
      if (this.isOpen) {
        this.closeDropdown();
      }
    });
  }

  closeDropdown() {
    this.isOpen = false;
    this.customSelect.querySelector('.select-trigger').classList.remove('active');
    this.customSelect.querySelector('.options-container').classList.remove('active');
  }

  updateOptions() {
    const optionsContainer = this.customSelect.querySelector('.options-container');
    const selected = this.customSelect.querySelector('.selected');

    optionsContainer.innerHTML = '';
    this.options = [];

    Array.from(this.originalSelect.options).forEach(option => {
      const optionElement = document.createElement('div');
      optionElement.className = 'option';
      optionElement.dataset.value = option.value;

      const textSpan = document.createElement('span');
      textSpan.textContent = option.text;
      textSpan.title = option.text;

      optionElement.appendChild(textSpan);

      if (option.selected) {
        optionElement.classList.add('selected');
        selected.textContent = option.text;
      }

      this.options.push(optionElement);
      optionsContainer.appendChild(optionElement);
    });

    this.bindEvents();
  }

  observeOriginalSelect() {
    const observer = new MutationObserver(() => {
      this.syncCustomSelect();
      if (this.originalSelect.disabled) {
        this.customSelect.classList.add('disabled');
      } else {
        this.customSelect.classList.remove('disabled');
      }
    });

    observer.observe(this.originalSelect, {
      attributes: true,
      childList: true,
      subtree: false,
    });
  }

  syncCustomSelect() {
    const selectedOption = this.originalSelect.options[this.originalSelect.selectedIndex];
    const selectedText = selectedOption ? selectedOption.text : 'Select an option';
    const selectedValue = selectedOption ? selectedOption.value : '';

    const selectedElement = this.customSelect.querySelector('.selected');
    selectedElement.textContent = selectedText;
    selectedElement.title = selectedText;

    this.options.forEach(optionElement => {
      if (optionElement.dataset.value === selectedValue) {
        optionElement.classList.add('selected');
      } else {
        optionElement.classList.remove('selected');
      }
    });
  }
}

(function initCustomSelectBox() {
	function initialize() {
		document.querySelectorAll('select.custom-select-box:not(.initialized)').forEach(select => {
			select.classList.add('initialized');
			new CustomSelectBox(select);
		});
	}

	function initObserver() {
		const observer = new MutationObserver((mutations) => {
			mutations.forEach((mutation) => {
				mutation.addedNodes.forEach((node) => {
					if (node.nodeType === 1) {
						if (node.matches('select.custom-select-box:not(.initialized)')) {
							node.classList.add('initialized');
							new CustomSelectBox(node);
						}

						node.querySelectorAll('select.custom-select-box:not(.initialized)').forEach(select => {
							select.classList.add('initialized');
							new CustomSelectBox(select);
						});
					}
				});
			});
		});

		if (document.body) {
			observer.observe(document.body, {
				childList: true,
				subtree: true
			});
		}
	}

	if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initialize();
      initObserver();
    });
	} else {
		initialize();
		initObserver();
  }
})();
